import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from './AuthContext';
import {
  FriendRequest,
  Friendship,
  FriendAccessLevel,
  PublicCruiseView,
  Notification,
} from '../types/friends';
import * as friendService from '../services/friendService';
import { AggregatedCruise, enrichFriendsWithCurrentUsernames } from '../services/friendService';
import * as notificationService from '../services/notificationService';
import toast from 'react-hot-toast';
import { debug } from '../utils/debug';
import { getTimestampMillis, getOptionalTimestampMillis } from '../utils/formatUtils';

interface FriendContextType {
  friends: Friendship[];
  friendRequests: FriendRequest[];
  sentFriendRequests: FriendRequest[];
  notifications: Notification[];
  loading: boolean;
  sendFriendRequest: (emailOrUsername: string, accessLevel: FriendAccessLevel) => Promise<void>;
  cancelFriendRequest: (requestId: string) => Promise<void>;
  respondToFriendRequest: (
    requestId: string,
    accept: boolean,
    yourAccessLevel?: FriendAccessLevel
  ) => Promise<void>;
  updateFriendAccessLevel: (friendId: string, accessLevel: FriendAccessLevel) => Promise<void>;
  removeFriend: (friendId: string) => Promise<void>;
  getFriendCruises: (friendId: string) => Promise<PublicCruiseView[]>;
  shareCruiseWithFriend: (cruiseId: string, friendId: string) => Promise<void>;
  unshareCruiseWithFriend: (cruiseId: string, friendId: string) => Promise<void>;
  getCruiseSharedWith: (cruiseId: string) => Promise<string[]>;
  getTravelingCompanions: (
    cruiseId: string,
    shipName: string,
    departureDate: string
  ) => Promise<Friendship[]>;
  getAllFriendsCruises: (includePast?: boolean) => Promise<AggregatedCruise[]>;
  inviteFriendToCruise: (friendId: string, cruise: any) => Promise<string>;
  acceptCruiseInvite: (notification: Notification) => Promise<string>;
  markNotificationRead: (notificationId: string) => Promise<void>;
  markAllNotificationsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
}

const FriendContext = createContext<FriendContextType | null>(null);

export const useFriends = () => {
  const context = useContext(FriendContext);
  if (!context) {
    throw new Error('useFriends must be used within a FriendProvider');
  }
  return context;
};

interface FriendProviderProps {
  children: ReactNode;
}

export const FriendProvider = ({ children }: FriendProviderProps) => {
  const [friends, setFriends] = useState<Friendship[]>([]);
  const [friendRequests, setFriendRequests] = useState<FriendRequest[]>([]);
  const [sentFriendRequests, setSentFriendRequests] = useState<FriendRequest[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();

  // Max time to wait for all 4 onSnapshot listeners to emit their first event.
  // On poor network (ship WiFi), individual listeners can stall; this ensures the
  // Friends page is never stuck in skeleton state indefinitely.
  const FRIEND_CONTEXT_TIMEOUT_MS = 4000;

  // Set up real-time listeners for friends, requests, and notifications
  useEffect(() => {
    if (!currentUser || !currentUser.emailVerified) {
      setFriends([]);
      setFriendRequests([]);
      setSentFriendRequests([]);
      setNotifications([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const userId = currentUser.uid;
    debug.log(`[FriendContext] Setting up listeners for user: ${userId}, emailVerified: ${currentUser.emailVerified}`);
    const unsubscribers: (() => void)[] = [];

    // Track which listeners have initialized (emitted at least once)
    const initializedListeners = new Set<string>();
    const allListeners = ['friends', 'requests', 'sentRequests', 'notifications'];

    // Safety timeout: if all listeners haven't fired within FRIEND_CONTEXT_TIMEOUT_MS,
    // unblock loading so the Friends page renders with whatever data is available.
    let friendsTimeoutId: ReturnType<typeof setTimeout> | null = setTimeout(() => {
      debug.warn('[FriendContext] Timeout reached — unblocking Friends page with available data');
      setLoading(false);
    }, FRIEND_CONTEXT_TIMEOUT_MS);
    
    const checkAllInitialized = () => {
      if (initializedListeners.size === allListeners.length) {
        if (friendsTimeoutId) {
          clearTimeout(friendsTimeoutId);
          friendsTimeoutId = null;
        }
        setLoading(false);
      }
    };

    // Listen to friends; enrich with current usernames from friendLookup so display
    // stays correct when a friend changes their username
    const friendsRef = collection(db, 'friendships', userId, 'friends');
    const friendsUnsub = onSnapshot(
      friendsRef,
      (snapshot) => {
        const rawFriends = snapshot.docs.map((doc) => ({
          ...doc.data(),
        })) as Friendship[];
        enrichFriendsWithCurrentUsernames(rawFriends)
          .then((enriched) => {
            setFriends(enriched);
            initializedListeners.add('friends');
            checkAllInitialized();
          })
          .catch((err) => {
            console.error('Error enriching friends with usernames:', err);
            setFriends(rawFriends);
            initializedListeners.add('friends');
            checkAllInitialized();
          });
      },
      (error) => {
        console.error('Error listening to friends:', error);
        initializedListeners.add('friends');
        checkAllInitialized();
      }
    );
    unsubscribers.push(friendsUnsub);

    // Listen to incoming friend requests
    const requestsRef = collection(db, 'friendRequests');
    const requestsQuery = query(
      requestsRef,
      where('toUserId', '==', userId),
      where('status', '==', 'pending')
    );
    debug.log(`[FriendContext] Setting up incoming requests listener for userId: ${userId}`);
    const requestsUnsub = onSnapshot(
      requestsQuery,
      (snapshot) => {
        debug.log(`[FriendContext] Received ${snapshot.docs.length} incoming friend requests for user ${userId}`);
        debug.log(`[FriendContext] Query metadata:`, {
          hasPendingWrites: snapshot.metadata.hasPendingWrites,
          fromCache: snapshot.metadata.fromCache,
          isEqual: snapshot.metadata.isEqual
        });
        
        // Log all matching documents
        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          debug.log(`[FriendContext] Request ${doc.id}:`, {
            fromUserId: data.fromUserId,
            toUserId: data.toUserId,
            status: data.status,
            createdAt: getTimestampMillis(data.createdAt),
            fromDisplayName: data.fromDisplayName,
            fromEmail: data.fromEmail
          });
        });
        
        const requestsData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: getTimestampMillis(data.createdAt),
            respondedAt: getOptionalTimestampMillis(data.respondedAt),
          };
        }) as FriendRequest[];
        setFriendRequests(requestsData);
        initializedListeners.add('requests');
        checkAllInitialized();
      },
      (error) => {
        console.error('[FriendContext] Error listening to friend requests:', error);
        // Log more details about the error
        console.error('[FriendContext] Error details:', {
          code: error.code,
          message: error.message,
          stack: error.stack,
          userId: userId
        });
        if (error.code === 'failed-precondition') {
          console.error('[FriendContext] Firestore index may be missing. Check FIRESTORE_INDEX.md for required indexes.');
          console.error('[FriendContext] Required index: friendRequests collection with fields: toUserId (ASC), status (ASC)');
        }
        if (error.code === 'permission-denied') {
          console.error('[FriendContext] Permission denied. Check Firestore security rules for friendRequests.');
          console.error('[FriendContext] User must be authenticated and email verified.');
        }
        // Still set empty array on error to prevent UI from hanging
        setFriendRequests([]);
        initializedListeners.add('requests');
        checkAllInitialized();
      }
    );
    unsubscribers.push(requestsUnsub);

    // Listen to sent friend requests
    const sentRequestsQuery = query(
      requestsRef,
      where('fromUserId', '==', userId),
      where('status', '==', 'pending')
    );
    const sentRequestsUnsub = onSnapshot(
      sentRequestsQuery,
      (snapshot) => {
        debug.log(`[FriendContext] Received ${snapshot.docs.length} sent friend requests for user ${userId}`);
        const sentRequestsData = snapshot.docs.map((doc) => {
          const data = doc.data();
          debug.log(`[FriendContext] Sent request ${doc.id}:`, {
            fromUserId: data.fromUserId,
            toUserId: data.toUserId,
            status: data.status,
            createdAt: getTimestampMillis(data.createdAt)
          });
          return {
            id: doc.id,
            ...data,
            createdAt: getTimestampMillis(data.createdAt),
            respondedAt: getOptionalTimestampMillis(data.respondedAt),
          };
        }) as FriendRequest[];
        setSentFriendRequests(sentRequestsData);
        initializedListeners.add('sentRequests');
        checkAllInitialized();
      },
      (error) => {
        console.error('Error listening to sent friend requests:', error);
        // Log more details about the error
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          stack: error.stack
        });
        if (error.code === 'failed-precondition') {
          console.error('Firestore index may be missing. Check FIRESTORE_INDEX.md for required indexes.');
          console.error('Required index: friendRequests collection with fields: fromUserId (ASC), status (ASC)');
        }
        // Still set empty array on error to prevent UI from hanging
        setSentFriendRequests([]);
        initializedListeners.add('sentRequests');
        checkAllInitialized();
      }
    );
    unsubscribers.push(sentRequestsUnsub);

    // Listen to notifications (retrieve recent 25 notifications including read history)
    const notificationsRef = collection(db, 'notifications', userId, 'notifications');
    const notificationsQuery = query(
      notificationsRef,
      orderBy('createdAt', 'desc'),
      limit(25)
    );
    const notificationsUnsub = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        const notificationsData = snapshot.docs.map((doc) => {
          const data = doc.data();
          const isCorrupted =
            data.createdAt &&
            typeof data.createdAt === 'object' &&
            data.createdAt._methodName === 'serverTimestamp';

          const fallbackTime = data.invitedCruiseData?.created || data.created || data.timestamp;
          const rawTime = isCorrupted ? fallbackTime : (data.createdAt ?? fallbackTime);

          return {
            id: doc.id,
            ...data,
            createdAt: getTimestampMillis(rawTime, fallbackTime),
          };
        }) as Notification[];
        notificationsData.sort((a, b) => b.createdAt - a.createdAt);
        setNotifications(notificationsData);
        initializedListeners.add('notifications');
        checkAllInitialized();
      },
      (error) => {
        console.error('Error listening to notifications:', error);
        initializedListeners.add('notifications');
        checkAllInitialized();
      }
    );
    unsubscribers.push(notificationsUnsub);

    return () => {
      if (friendsTimeoutId) clearTimeout(friendsTimeoutId);
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [currentUser]);

  const sendFriendRequest = async (
    emailOrUsername: string,
    accessLevel: FriendAccessLevel
  ): Promise<void> => {
    if (!currentUser) throw new Error('You must be logged in');
    const fromEmail = currentUser.email;
    if (!fromEmail) throw new Error('Your account must have an email to send friend requests');
    try {
      await friendService.sendFriendRequest(
        currentUser.uid,
        fromEmail,
        emailOrUsername,
        accessLevel
      );
      toast.success('Friend request sent!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to send friend request');
      throw error;
    }
  };

  const cancelFriendRequest = async (requestId: string): Promise<void> => {
    if (!currentUser) throw new Error('You must be logged in');
    try {
      await friendService.cancelFriendRequest(requestId, currentUser.uid);
      toast.success('Friend request cancelled');
    } catch (error: any) {
      toast.error(error.message || 'Failed to cancel friend request');
      throw error;
    }
  };

  const respondToFriendRequest = async (
    requestId: string,
    accept: boolean,
    yourAccessLevel?: FriendAccessLevel
  ): Promise<void> => {
    if (!currentUser) throw new Error('You must be logged in');
    try {
      await friendService.respondToFriendRequest(
        requestId,
        currentUser.uid,
        accept,
        yourAccessLevel || 'restricted-access'
      );
      if (accept) {
        toast.success('Friend request accepted!');
      } else {
        toast.success('Friend request rejected');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to respond to friend request');
      throw error;
    }
  };

  const updateFriendAccessLevel = async (
    friendId: string,
    accessLevel: FriendAccessLevel
  ): Promise<void> => {
    if (!currentUser) throw new Error('You must be logged in');
    try {
      await friendService.updateFriendAccessLevel(currentUser.uid, friendId, accessLevel);
      toast.success('Friend access level updated');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update friend access level');
      throw error;
    }
  };

  const removeFriend = async (friendId: string): Promise<void> => {
    if (!currentUser) throw new Error('You must be logged in');
    try {
      await friendService.removeFriend(currentUser.uid, friendId);
      toast.success('Friend removed');
    } catch (error: any) {
      toast.error(error.message || 'Failed to remove friend');
      throw error;
    }
  };

  const getFriendCruises = async (friendId: string): Promise<PublicCruiseView[]> => {
    if (!currentUser) throw new Error('You must be logged in');
    try {
      return await friendService.getFriendCruises(currentUser.uid, friendId);
    } catch (error: any) {
      toast.error(error.message || 'Failed to get friend cruises');
      throw error;
    }
  };

  const shareCruiseWithFriend = async (
    cruiseId: string,
    friendId: string
  ): Promise<void> => {
    if (!currentUser) throw new Error('You must be logged in');
    try {
      await friendService.shareCruiseWithFriend(currentUser.uid, cruiseId, friendId);
      toast.success('Cruise shared with friend');
    } catch (error: any) {
      toast.error(error.message || 'Failed to share cruise');
      throw error;
    }
  };

  const unshareCruiseWithFriend = async (
    cruiseId: string,
    friendId: string
  ): Promise<void> => {
    if (!currentUser) throw new Error('You must be logged in');
    try {
      await friendService.unshareCruiseWithFriend(currentUser.uid, cruiseId, friendId);
      toast.success('Cruise unshared');
    } catch (error: any) {
      toast.error(error.message || 'Failed to unshare cruise');
      throw error;
    }
  };

  const getSharedCruises = async (friendId: string): Promise<string[]> => {
    if (!currentUser) throw new Error('You must be logged in');
    try {
      return await friendService.getSharedCruises(currentUser.uid, friendId);
    } catch (error: any) {
      console.error('Error getting shared cruises:', error);
      return [];
    }
  };

  const getCruiseSharedWith = async (cruiseId: string): Promise<string[]> => {
    if (!currentUser) return [];
    try {
      return await friendService.getCruiseSharedWith(currentUser.uid, cruiseId);
    } catch (error: any) {
      console.error('Error getting cruise sharedWith:', error);
      return [];
    }
  };

  const getTravelingCompanions = async (
    cruiseId: string,
    shipName: string,
    departureDate: string
  ): Promise<Friendship[]> => {
    if (!currentUser) throw new Error('You must be logged in');
    try {
      return await friendService.getTravelingCompanions(
        currentUser.uid,
        cruiseId,
        shipName,
        departureDate
      );
    } catch (error: any) {
      console.error('Error getting traveling companions:', error);
      return [];
    }
  };

  const getAllFriendsCruises = useCallback(async (includePast: boolean = false): Promise<AggregatedCruise[]> => {
    if (!currentUser) throw new Error('You must be logged in');
    try {
      return await friendService.getAllFriendsCruises(currentUser.uid, includePast);
    } catch (error: any) {
      toast.error(error.message || 'Failed to get friends cruises');
      throw error;
    }
  }, [currentUser]);

  const inviteFriendToCruise = async (friendId: string, cruise: any): Promise<string> => {
    if (!currentUser) throw new Error('You must be logged in');
    try {
      const fromName = currentUser.displayName || currentUser.email || 'A friend';
      const notificationId = await friendService.inviteFriendToCruise(
        currentUser.uid,
        fromName,
        friendId,
        cruise
      );
      if (notificationId === 'already-invited') {
        toast('Invitation already sent to this friend');
      } else {
        toast.success('Cruise invitation sent!');
      }
      return notificationId;
    } catch (error: any) {
      toast.error(error.message || 'Failed to send cruise invitation');
      throw error;
    }
  };

  const acceptCruiseInvite = async (notification: Notification): Promise<string> => {
    if (!currentUser) throw new Error('You must be logged in');
    try {
      const newCruiseId = await friendService.acceptCruiseInvite(currentUser.uid, notification);
      toast.success('Cruise added to your itinerary!');
      // Remove accepted invite from notifications list locally
      setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
      return newCruiseId;
    } catch (error: any) {
      toast.error(error.message || 'Failed to accept cruise invitation');
      throw error;
    }
  };

  const markNotificationRead = async (notificationId: string): Promise<void> => {
    if (!currentUser) throw new Error('You must be logged in');
    try {
      await notificationService.markNotificationRead(currentUser.uid, notificationId);
    } catch (error: any) {
      toast.error('Failed to mark notification as read');
      throw error;
    }
  };

  const markAllNotificationsRead = async (): Promise<void> => {
    if (!currentUser) throw new Error('You must be logged in');
    try {
      await notificationService.markAllNotificationsRead(currentUser.uid);
    } catch (error: any) {
      toast.error('Failed to mark all notifications as read');
      throw error;
    }
  };

  const deleteNotification = async (notificationId: string): Promise<void> => {
    if (!currentUser) throw new Error('You must be logged in');
    try {
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      await notificationService.deleteNotification(currentUser.uid, notificationId);
    } catch (error: any) {
      toast.error('Failed to delete notification');
      throw error;
    }
  };

  const value = {
    friends,
    friendRequests,
    sentFriendRequests,
    notifications,
    loading,
    sendFriendRequest,
    cancelFriendRequest,
    respondToFriendRequest,
    updateFriendAccessLevel,
    removeFriend,
    getFriendCruises,
    shareCruiseWithFriend,
    unshareCruiseWithFriend,
    getCruiseSharedWith,
    getSharedCruises,
    getTravelingCompanions,
    getAllFriendsCruises,
    inviteFriendToCruise,
    acceptCruiseInvite,
    markNotificationRead,
    markAllNotificationsRead,
    deleteNotification,
  };

  return <FriendContext.Provider value={value}>{children}</FriendContext.Provider>;
};

